import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const protectionPoints = [
  {
    id: 1,
    title: 'UV & Sun Protection',
    description: 'Ceramic coating blocks harmful UV rays that cause paint oxidation, fading, and deterioration over time.',
    position: { top: '75%', left: '18%' }
  },
  {
    id: 2,
    title: 'Industrial Fallout',
    description: 'Protects against metal particles, brake dust, and industrial pollutants that embed in your paint.',
    position: { top: '68%', left: '32%' }
  },
  {
    id: 3,
    title: 'Weather Erosion',
    description: 'Shields your paint from harsh weather conditions including rain, snow, and extreme temperatures.',
    position: { top: '55%', left: '45%' }
  },
  {
    id: 4,
    title: 'Acid Rain',
    description: 'Creates a chemical-resistant barrier against acid rain and environmental contaminants.',
    position: { top: '65%', left: '55%' }
  },
  {
    id: 5,
    title: 'Bird Droppings',
    description: 'Hydrophobic surface prevents bird droppings from etching into your clear coat.',
    position: { top: '58%', left: '68%' }
  },
  {
    id: 6,
    title: 'Tree Sap',
    description: 'Repels tree sap and sticky residues, making them easy to wash off without damage.',
    position: { top: '72%', left: '82%' }
  }
];

export const CeramicProtectionInfographic = () => {
  const [activePoint, setActivePoint] = useState(null);

  const handlePointClick = (e, pointId) => {
    e.stopPropagation();
    setActivePoint(activePoint === pointId ? null : pointId);
  };

  const closePopup = (e) => {
    e.stopPropagation();
    setActivePoint(null);
  };

  return (
    <section className="section-padding bg-black relative overflow-hidden" data-testid="ceramic-infographic">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4">
            Why Choose Ceramic Coating?
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Professional ceramic coating creates an invisible shield that protects your vehicle from daily environmental hazards. Click the glowing points to learn more.
          </p>
        </div>

        {/* Infographic Container */}
        <div className="relative max-w-5xl mx-auto">
          {/* Car Image with Hotspots */}
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_c0fed34f-5ae2-4789-b2ca-6224f846e8eb/artifacts/t45czrid_why-Choose-ceramic-coating-1536x864-1.webp"
              alt="Ceramic coating protection - click the glowing points to learn about UV, industrial fallout, weather, acid rain, bird droppings, and tree sap protection"
              className="w-full h-auto rounded-lg"
            />
            
            {/* Glowing Hotspot Buttons */}
            {protectionPoints.map((point) => (
              <button
                key={point.id}
                onClick={(e) => handlePointClick(e, point.id)}
                className={`absolute w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 rounded-full cursor-pointer z-10 transition-all duration-300 ${
                  activePoint === point.id 
                    ? 'bg-cyan-400 shadow-[0_0_30px_10px_rgba(6,182,212,0.8)]' 
                    : 'bg-cyan-500/80 animate-pulse-glow hover:scale-125'
                }`}
                style={{ 
                  top: point.position.top, 
                  left: point.position.left,
                  transform: 'translate(-50%, -50%)'
                }}
                data-testid={`hotspot-${point.id}`}
                aria-label={point.title}
              >
                <span className="sr-only">{point.title}</span>
              </button>
            ))}
          </div>

          {/* Fixed Position Popup - Centered on screen for mobile */}
          <AnimatePresence>
            {activePoint && (
              <>
                {/* Backdrop overlay */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/70 z-[50]"
                  onClick={closePopup}
                />
                
                {/* Popup wrapper for centering */}
                <div 
                  className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none"
                  onClick={closePopup}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    className="w-full max-w-sm bg-black border border-cyan-500/50 rounded-lg p-5 shadow-2xl pointer-events-auto"
                    data-testid="hotspot-popup"
                    onClick={(e) => e.stopPropagation()}
                  >
                  <button 
                    onClick={closePopup}
                    className="absolute top-3 right-3 p-1 hover:bg-white/10 rounded-full transition-colors"
                    aria-label="Close popup"
                  >
                    <X className="w-5 h-5 text-gray-400 hover:text-white" />
                  </button>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0 animate-pulse">
                      <div className="w-4 h-4 rounded-full bg-white" />
                    </div>
                    <div className="pr-6">
                      <h3 className="font-heading text-lg font-semibold uppercase tracking-wider text-cyan-400 mb-2">
                        {protectionPoints.find(p => p.id === activePoint)?.title}
                      </h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {protectionPoints.find(p => p.id === activePoint)?.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
                </div>
              </>
            )}
          </AnimatePresence>

          {/* Bottom CTA */}
          <div className="text-center mt-10">
            <p className="text-gray-400 mb-4">
              Protect your investment with professional ceramic coating in Fresno & Central California
            </p>
            <a 
              href="tel:5598801818"
              className="btn-primary inline-flex items-center gap-2"
            >
              Get Protected Today - Call 559-880-1818
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
